import React from 'react';
import { Table, Pagination, Form } from 'react-bootstrap';
import './DataTable.css';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  sortValue?: (item: T) => any;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  emptyMessage?: string;
  itemsPerPageOptions?: number[];
  className?: string;
  /** Classes aplicadas ao `<Table>` (ex.: estilos do dashboard) */
  tableClassName?: string;
}

function DataTable<T extends { id: number | string }>({
  data,
  columns,
  loading = false,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  sortColumn,
  sortDirection = 'desc',
  onSort,
  emptyMessage = 'Nenhum item encontrado',
  itemsPerPageOptions = [5, 10, 20, 50, 100],
  className = '',
  tableClassName = '',
}: DataTableProps<T>) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getSortIcon = (columnKey: string) => {
    if (!onSort || sortColumn !== columnKey) {
      return (
        <span className="sort-icon ms-2" aria-hidden="true">
          ↕
        </span>
      );
    }
    return (
      <span className="sort-icon active ms-2" aria-hidden="true">
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  // Calcular quais páginas mostrar
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <small className="text-muted">
            {totalItems === 0 
              ? 'Nenhum item encontrado'
              : `Mostrando ${startItem} a ${endItem} de ${totalItems} itens`}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0" style={{ fontSize: '0.875rem' }}>
            Itens por página:
          </Form.Label>
          <Form.Select
            value={itemsPerPage}
            onChange={(e) => {
              onItemsPerPageChange(Number(e.target.value));
              onPageChange(1); // Resetar para primeira página
            }}
            style={{ width: 'auto' }}
            size="sm"
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="empty-state text-center py-5">
          <p className="text-muted">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <Table responsive hover className={tableClassName || undefined}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th 
                    key={column.key}
                    style={column.key === 'actions' ? { minWidth: '150px', whiteSpace: 'nowrap' } : undefined}
                  >
                    {column.sortable && onSort ? (
                      <div
                        className={`sortable-header d-flex align-items-center ${
                          sortColumn === column.key ? 'active' : ''
                        }`}
                        onClick={() => handleSort(column.key)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {column.label}
                        {getSortIcon(column.key)}
                      </div>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  {columns.map((column) => (
                    <td 
                      key={column.key}
                      style={column.key === 'actions' ? { minWidth: '150px', whiteSpace: 'nowrap' } : undefined}
                    >
                      {column.render ? column.render(item) : (item as any)[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Página {currentPage} de {totalPages}
                </small>
              </div>
              <Pagination>
                <Pagination.First
                  onClick={() => onPageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                />

                {getPageNumbers().map((pageNum) => (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                ))}

                <Pagination.Next
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                />
                <Pagination.Last
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DataTable;

